import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function Invoice() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => setErr("Not found"));
  }, [id]);

  if (err) return <div className="p-20 pt-40 text-center">Booking not found.</div>;
  if (!b) return <div className="p-20 pt-40 text-center text-[#6B665E]">Loading…</div>;

  const total = Number(b.total_price || b.price || 0);
  const deposit = Number(b.deposit_paid_amount || b.deposit_amount || 0);
  const balance = Number(b.balance_due || Math.max(total - deposit, 0));
  const paidLabel = b.payment_status === "paid" ? "Paid in full" : b.payment_status === "deposit_paid" ? "Deposit paid — balance due on the day" : "Unpaid";

  return (
    <div data-testid="invoice-page" className="bg-[#F5EFE4] min-h-[100vh] py-16 print:py-0 print:bg-white">
      <div className="max-w-[820px] mx-auto px-6 print:px-0">
        <div className="flex justify-between items-center mb-6 print:hidden">
          <Link to="/dashboard" className="text-xs uppercase tracking-[0.22em] text-[#6B665E] hover:text-[#C5A059] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> Back to Dashboard
          </Link>
          <button onClick={() => window.print()} className="luxor-btn-primary" data-testid="invoice-print">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>

        <div className="bg-white border border-[#E4D9C1] p-10 print:border-0 print:p-0" data-testid="invoice-body">
          <div className="flex justify-between items-start pb-8 border-b border-[#E4D9C1]">
            <div>
              <p className="overline">Luxor Services &amp; Concierge</p>
              <h1 className="font-serif text-4xl mt-3 text-[#1A1815]">Invoice / Receipt</h1>
              <p className="text-xs text-[#6B665E] mt-2">Reference: {b.id}</p>
              <p className="text-xs text-[#6B665E]">Issued: {new Date(b.created_at).toLocaleDateString("en-GB")}</p>
            </div>
            <div className="text-right text-xs text-[#6B665E]">
              <p className="text-[#1A1815] font-medium">Luxor Services &amp; Concierge Ltd</p>
              <p>London, United Kingdom</p>
              <p>info@luxorservicesconcierge.com</p>
              <p>+44 1234 567 890</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 py-8 border-b border-[#E4D9C1]">
            <div>
              <p className="overline">Billed to</p>
              <p className="text-[#1A1815] font-medium mt-3">{b.customer_name}</p>
              <p className="text-sm text-[#6B665E]">{b.customer_email}</p>
              {b.customer_phone && <p className="text-sm text-[#6B665E]">{b.customer_phone}</p>}
              <p className="text-sm text-[#6B665E] mt-2">{b.location}</p>
            </div>
            <div className="text-right">
              <p className="overline">Service</p>
              <p className="text-[#1A1815] font-medium mt-3">{b.service_name}</p>
              <p className="text-sm text-[#6B665E]">{b.package_name}</p>
              <p className="text-sm text-[#6B665E] mt-2">{b.date} · {b.time}</p>
            </div>
          </div>

          <table className="w-full mt-8 text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-[0.18em] text-[#6B665E] border-b border-[#E4D9C1]">
                <th className="text-left py-3">Item</th>
                <th className="text-right py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#E4D9C1]">
                <td className="py-4 text-[#1A1815]">
                  {b.service_name} — {b.package_name}
                  <div className="text-xs text-[#6B665E] mt-1">Booking on {b.date} at {b.time}</div>
                </td>
                <td className="py-4 text-right text-[#1A1815]">£{total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <table className="w-full mt-8 text-sm">
            <tbody>
              <tr><td className="py-2 text-[#6B665E]">Subtotal</td><td className="py-2 text-right text-[#1A1815]">£{total.toFixed(2)}</td></tr>
              <tr><td className="py-2 text-[#6B665E]">Deposit paid</td><td className="py-2 text-right text-[#C5A059]">−£{deposit.toFixed(2)}</td></tr>
              <tr className="border-t border-[#E4D9C1]"><td className="py-3 font-medium">Balance due on the day</td><td className="py-3 text-right font-serif text-2xl text-[#1A1815]">£{balance.toFixed(2)}</td></tr>
            </tbody>
          </table>

          <div className="mt-8 p-4 bg-[#F5EFE4] text-sm text-[#4A453E] border-l-2 border-[#C5A059]">
            <strong>Payment status:</strong> {paidLabel}. Balance is payable on the day of service in cash or by card. A full quote was agreed in advance.
          </div>

          <p className="mt-10 text-xs text-[#6B665E] text-center">
            Thank you for choosing Luxor Services &amp; Concierge.
          </p>
        </div>
      </div>
    </div>
  );
}
